import { useState, type ReactNode } from 'react';

export function PageHead({ eyebrow, title, subtitle, actions }: { eyebrow: string; title: string; subtitle: string; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div><span className="eyebrow">{eyebrow}</span><h1 id="page-title">{title}</h1><p>{subtitle}</p></div>
      {actions && <div className="head-actions">{actions}</div>}
    </div>
  );
}

export function DataNote({ children }: { children: ReactNode }) {
  return <div className="data-note"><i aria-hidden="true" />{children}</div>;
}

export type Tone = 'mint' | 'amber' | 'gray' | 'critical' | 'default' | 'opportunity' | 'action';

export function StatusPill({ tone, children }: { tone: string; children: ReactNode }) {
  const cls = tone === 'amber' ? 'amber' : tone === 'gray' ? 'gray' : tone === 'critical' ? 'critical' : '';
  return <span className={`status ${cls}`}>{children}</span>;
}

export function CompanyCell({ initials, name, meta }: { initials: string; name: string; meta: string }) {
  return <div className="company-cell"><div className="company" aria-hidden="true">{initials}</div><div><b>{name}</b><span>{meta}</span></div></div>;
}

export function MetricCard({ label, value, trend, tone, spark, color }: { label: string; value: string; trend: string; tone: string; spark: string; color: string }) {
  return (
    <article className={`metric ${tone === 'opportunity' ? 'opportunity' : tone === 'action' ? 'action' : ''}`}>
      <small>{label}</small>
      <strong>{value}</strong>
      <span className="trend">{trend}</span>
      <svg className="spark" viewBox="0 0 70 30" aria-hidden="true"><path d={spark} fill="none" stroke={color} strokeWidth="2.5" /></svg>
    </article>
  );
}

export function FilterBar<T extends string>({ options, value, onChange, label }: { options: Array<{ value: T; label: string }>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="filters" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="chip" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function initials(name: string): string {
  return name.split(/\s+/).filter((p) => p.length > 2 || /^[A-Z]/.test(p)).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || name.slice(0, 2).toUpperCase();
}

/** Gráfico de evolução (TrendChart): menta = série principal, âmbar tracejado = comparação; resumo textual equivalente. */
export function TrendChart({ labels, primary, secondary, primaryLabel, secondaryLabel }: { labels: readonly string[]; primary: readonly number[]; secondary: readonly number[]; primaryLabel: string; secondaryLabel: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 700, H = 250, max = Math.max(...primary, ...secondary) * 1.12;
  const pts = (s: readonly number[]) => s.map((v, i) => [(i / (s.length - 1)) * W, H - (v / max) * (H - 20) - 8] as const);
  const smooth = (p: ReadonlyArray<readonly [number, number]>) => p.reduce((d, [x, y], i) => {
    if (i === 0) return `M${x} ${y}`;
    const [px, py] = p[i - 1]!;
    const cx = (px + x) / 2;
    return `${d} C${cx} ${py} ${cx} ${y} ${x} ${y}`;
  }, '');
  const p1 = pts(primary), p2 = pts(secondary);
  const line = smooth(p1);
  const first = primary[0]!, last = primary.at(-1)!;
  const summary = `${primaryLabel} passaram de ${first} para ${last} entre ${labels[0]} e ${labels.at(-1)} (alta de ${Math.round(((last - first) / first) * 100)}%). ${secondaryLabel}: de ${secondary[0]} para ${secondary.at(-1)}.`;
  return (
    <figure style={{ margin: 0 }}>
      <div className="chart-area" onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={summary}>
          <defs><linearGradient id="dashFill" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#6df0cb" stopOpacity=".42" /><stop offset="1" stopColor="#6df0cb" stopOpacity=".02" /></linearGradient></defs>
          <path d={`${line} L${W} ${H}H0Z`} fill="url(#dashFill)" />
          <path d={line} fill="none" stroke="#13a98b" strokeWidth="3" vectorEffect="non-scaling-stroke" />
          <path d={smooth(p2)} fill="none" stroke="#fdc44f" strokeWidth="2.5" strokeDasharray="6 7" vectorEffect="non-scaling-stroke" />
          {p1.map(([x], i) => (
            <rect key={i} x={x - W / (p1.length - 1) / 2} y={0} width={W / (p1.length - 1)} height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hover !== null && (
          <div className="chart-tip" style={{ left: `${(hover / (p1.length - 1)) * 100}%`, top: `${(p1[hover]![1] / H) * 100}%` }}>
            Semana {hover + 1}: <b>{primary[hover]}</b> qualificadas · {secondary[hover]} sinais
          </div>
        )}
      </div>
      <div className="chart-labels" aria-hidden="true">{labels.map((l) => <span key={l}>{l}</span>)}</div>
      <div className="legend"><span><i className="m" />{primaryLabel}</span><span><i className="a" />{secondaryLabel}</span></div>
      <figcaption className="chart-summary">{summary}</figcaption>
    </figure>
  );
}
